import { replaceEmptyStringsWithNull } from "@/shared/utils/replace-empty-strings-with-null";
import { contactsActions } from "@/features/contacts/model/slice/contacts-slice";
import { useQueuedMutation } from "@/services/api/hooks";
import type {
  CreatePersonResponse,
  CreatePersonRequest,
  DeletePersonResponse,
  GetAllPersonsResponse,
  SyncPersonsResponse,
  UpdatePersonResponse,
  UpdatePersonRequest,
  GetPersonByIdResponse,
  GetPersonByIdRequest,
  UpdatePersonAvatarResponse,
  UpdatePersonAvatarRequest,
  DeletePersonAvatarResponse,
  DeletePersonAvatarRequest,
} from "../types";
import { rootApi } from "../../../api";

export const personApi = rootApi.injectEndpoints({
  endpoints: (build) => ({
    getAllPersons: build.query<GetAllPersonsResponse, void>({
      query: () => ({
        url: "person/all",
        method: "GET",
      }),
      providesTags: ["person"],
    }),
    createPerson: build.mutation<CreatePersonResponse, CreatePersonRequest>({
      query: (personData) => {
        const processedPersonData = replaceEmptyStringsWithNull(personData);
        return {
          url: "person/create",
          method: "POST",
          body: {
            ...processedPersonData,
            id: undefined,
          },
        };
      },
      invalidatesTags: ["sync", "calendarEvent"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(contactsActions.setSelectedPersonId(data.id));
        } catch (error) {
          console.error("Ошибка при создании пользователя:", error);
        }
      },
    }),
    getPersonById: build.query<GetPersonByIdResponse, GetPersonByIdRequest>({
      query: ({ id }) => ({
        url: `person/${id}`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
      providesTags: ["personInfo"],
    }),
    deletePerson: build.mutation<DeletePersonResponse, string>({
      query: (personId) => ({
        url: `person/delete/${personId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["person", "calendarEvent", "search", "personInfo"],
    }),
    syncPersons: build.query<SyncPersonsResponse, string>({
      query: (lastSyncTimestamp) => ({
        url: "person/sync",
        method: "GET",
        params: { last_sync: lastSyncTimestamp },
      }),
      providesTags: ["person", "sync"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const { deletedIds, personsDto } = data;

          dispatch(
            personApi.util.updateQueryData("getAllPersons", undefined, (draft) => {
              draft.persons = draft.persons.filter((person) => !deletedIds.includes(person.id));

              personsDto.persons.forEach((updatedPerson) => {
                const index = draft.persons.findIndex((person) => person.id === updatedPerson.id);
                if (index !== -1) {
                  draft.persons[index] = updatedPerson;
                } else {
                  draft.persons.push(updatedPerson);
                }
              });
              draft.syncDate = personsDto.syncDate;
              draft.count = draft.persons.length;
            }),
          );
        } catch (error) {
          console.error("Ошибка при синхронизации:", error);
        }
      },
    }),
    updatePerson: build.mutation<UpdatePersonResponse, UpdatePersonRequest>({
      query: (data) => {
        const processedData = replaceEmptyStringsWithNull(data);
        return {
          url: "person/update",
          method: "PUT",
          body: processedData,
        };
      },
      invalidatesTags: ["calendarEvent", "sync"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedDataFromServer } = await queryFulfilled;

          dispatch(
            personApi.util.updateQueryData(
              "getPersonById",
              { id: updatedDataFromServer.id },
              (draft) => {
                Object.assign(draft, updatedDataFromServer);
              },
            ),
          );
        } catch (error) {
          console.error("Ошибка при обновлении кэша getPersonById:", error);
        }
      },
    }),
    updatePersonAvatar: build.mutation<UpdatePersonAvatarResponse, UpdatePersonAvatarRequest>({
      query: (data) => {
        return {
          url: "person/avatar",
          method: "PUT",
          body: data,
        };
      },
      invalidatesTags: ["calendarEvent", "sync"],
    }),
    deletePersonAvatar: build.mutation<DeletePersonAvatarResponse, DeletePersonAvatarRequest>({
      query: (data) => {
        return {
          url: "person/avatar",
          method: "DELETE",
          body: data,
        };
      },
      invalidatesTags: ["calendarEvent", "sync"],
    }),
  }),
});

export const {
  useGetAllPersonsQuery,
  useCreatePersonMutation,
  useDeletePersonMutation,
  useSyncPersonsQuery,
  useUpdatePersonMutation,
  useLazyGetPersonByIdQuery,
  useGetPersonByIdQuery,
  useDeletePersonAvatarMutation,
  useUpdatePersonAvatarMutation,
} = personApi;

export const useUpdatePersonSafeMutation = () => {
  const [updatePerson] = personApi.useUpdatePersonMutation();
  return useQueuedMutation<UpdatePersonRequest, UpdatePersonResponse>(updatePerson);
};
